import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import { defineStore } from "pinia";
import { getCurrentAuthToken } from "./authToken";

const INACTIVE_CONNECTION_STATE = "inactive";
let eventHub = null;

function getConnectionState(connection = eventHub) {
  return connection ? connection.state : INACTIVE_CONNECTION_STATE;
}

function isConnected(connection = eventHub) {
  return connection != null && connection.state === HubConnectionState.Connected;
}

function getAccessToken() {
  return getCurrentAuthToken()?.access_token;
}

export const useRealtimeStore = defineStore("realtime", {
  state: () => ({
    connectionState: INACTIVE_CONNECTION_STATE,
    shutdown: null,
    connectionId: null,
    notifications: [],
    sharedroutelist: [],
    activeroute: {},
    messageHistory: [],
  }),

  actions: {
    setActiveRoute(route) {
      this.activeroute = route;
    },

    pushSharedRoute(route) {
      const itemIndex = this.sharedroutelist.findIndex((item) => item.connectionId === route.connectionId);

      if (itemIndex >= 0) {
        if (route.path !== this.activeroute.Path) {
          this.sharedroutelist.splice(itemIndex, 1);
        }
      } else if (route.path === this.activeroute.Path) {
        this.sharedroutelist.push(route);
      }
    },

    addNotification(notification) {
      this.notifications.push(notification);
    },

    removeNotification(taskId) {
      const itemIndex = this.notifications.findIndex((item) => item.taskId == taskId);
      if (itemIndex >= 0) {
        this.notifications.splice(itemIndex, 1);
      }
    },

    updateNotification(payload) {
      const itemIndex = this.notifications.findIndex((item) => item.taskId == payload.index);
      if (itemIndex >= 0) {
        this.notifications.splice(itemIndex, 1, {
          ...this.notifications[itemIndex],
          ...payload.content,
        });
      }
    },

    clearNotifications() {
      this.notifications = [];
    },

    updateConnectionId(connectionId) {
      this.connectionId = connectionId;
      this.connectionState = getConnectionState();
    },

    setConnectionState(connectionState) {
      this.connectionState = connectionState;
    },

    resetConnection() {
      this.connectionId = null;
      this.connectionState = INACTIVE_CONNECTION_STATE;
    },

    setShutdown(args) {
      this.shutdown = args;
    },

    route(payload) {
      this.setActiveRoute(payload);
      if (isConnected() && this.connectionId) {
        eventHub.invoke("Route", payload);
      }
    },

    message(payload) {
      if (isConnected() && this.connectionId) {
        eventHub.invoke("Message", payload);
      }
    },

    subscribe(payload) {
      if (eventHub) {
        eventHub.on(payload[0], payload[1]);
      } else {
        console.log("A component attempted to subscribe to event hub while not connected");
      }
    },

    unsubscribe(payload) {
      if (eventHub) {
        eventHub.off(payload[0], payload[1]);
      } else {
        console.log("A component attempted to unsubscribe to event hub while not connected");
      }
    },

    async disconnect() {
      const currentEventHub = eventHub;
      eventHub = null;
      this.resetConnection();

      if (currentEventHub != null) {
        try {
          await currentEventHub.stop();
        } catch (error) {
          console.error("Failed to stop websocket connection", error);
        }
      }
    },

    async connect() {
      if (eventHub != null) {
        this.setConnectionState(getConnectionState(eventHub));

        if (eventHub.state === HubConnectionState.Disconnected && getAccessToken()) {
          try {
            await eventHub.start();
            this.setConnectionState(getConnectionState(eventHub));
          } catch (error) {
            console.log("Websocket failed to restart: ", error);
          }
        }
        return;
      }

      await this.disconnect();

      const connection = new HubConnectionBuilder()
        .withUrl("/events/customerportal", {
          accessTokenFactory: () => getAccessToken(),
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (context) => {
            console.log("Re-trying websocket connection...");
            this.setConnectionState(getConnectionState(connection));
            return context.elapsedMilliseconds < 120000 ? 3000 : 60000;
          },
        })
        .configureLogging(LogLevel.Error)
        .build();

      eventHub = connection;
      this.setConnectionState(getConnectionState(connection));

      connection.on("connectionid", (args) => {
        if (this.shutdown) {
          this.setShutdown(null);
          window.location.reload();
        }
        this.updateConnectionId(args);
      });

      connection.on("shutdown", (args) => {
        this.setShutdown(args);
      });

      connection.on("tasks", (args) => {
        this.addNotification(args);
      });

      connection.onreconnecting(() => {
        this.setConnectionState(getConnectionState(connection));
      });

      connection.onreconnected((id) => {
        this.updateConnectionId(id);
        this.setConnectionState(getConnectionState(connection));
        if (this.shutdown) {
          this.setShutdown(null);
          window.location.reload();
        }
        this.clearNotifications();
      });

      connection.onclose(() => {
        this.setConnectionState(getConnectionState(connection));
      });

      const startHub = () => {
        if (getAccessToken() && eventHub === connection) {
          connection
            .start()
            .then(() => {
              this.setConnectionState(getConnectionState(connection));
              if (this.activeroute.Path) {
                connection.invoke("Route", this.activeroute);
              }
            })
            .catch((error) => {
              console.log("Websocket failed to start: ", error);
              connection.stop().finally(() => {
                this.setConnectionState(getConnectionState(connection));
              });
              setTimeout(() => {
                console.log("Retrying websocket connection");
                startHub();
              }, 3000);
            });
        }
      };

      startHub();
    },
  },
});
